import type { NpcFaction, NpcHullTier } from "../types";

// Canvas-drawn ship silhouettes (design doc Section 4b extension,
// 16.79) -- the Expedition View's own version of 16.78's DOM/CSS icon
// system. The Expedition View draws every object on `<canvas>`, so the
// same shape LANGUAGE (shape by role/subtype, plating by tier, a
// centered Roman numeral, hue by allegiance) has to be re-expressed as
// path point tables instead of clip-path percentages -- but it is
// deliberately the *same* language, not a second one invented from
// scratch, so a hull reads consistently whether you're looking at it on
// the Shipyard or flying near it.
//
// **Coordinate convention.** Every point table is in the LOCAL frame
// `shipShape()`'s original triangle already used: nose at local +x
// (i.e. `(1, 0)` before scaling by `size`), rotated by the ship's real
// heading. Converting the CSS shapes (nose at "50% 0%", y=0 at the top)
// into this frame is a fixed remap applied once, here, rather than at
// every call site: `localX = (0.5 - cssY) * 2`, `localY = (cssX - 0.5) * 2`.

export type PolygonPoint = [number, number]; // local units, -1..1, nose at (1, 0)

function fromCssPercent(points: [number, number][]): PolygonPoint[] {
  return points.map(([cssX, cssY]) => [(50 - cssY) / 50, (cssX - 50) / 50]);
}

// ---- Player hull shapes, one per role (16.16's 6 roles) --------------
// Identical polygons to 16.78's CSS clip-paths (style.css's
// .ship-icon-hull.{role}), just remapped into this canvas frame.
export const ROLE_POLYGON: Record<string, PolygonPoint[]> = {
  Digger: fromCssPercent([[50,0],[78,18],[92,42],[100,58],[78,58],[70,78],[50,100],[30,78],[22,58],[0,58],[8,42],[22,18]]),
  Packmule: fromCssPercent([[50,0],[62,10],[62,30],[92,34],[96,55],[92,76],[62,80],[62,96],[50,100],[38,96],[38,80],[8,76],[4,55],[8,34],[38,30],[38,10]]),
  Kestrel: fromCssPercent([[50,0],[58,32],[100,70],[78,78],[58,62],[58,100],[42,100],[42,62],[22,78],[0,70],[42,32]]),
  Journeyman: fromCssPercent([[50,0],[90,26],[90,74],[50,100],[10,74],[10,26]]),
  Wildcat: fromCssPercent([[50,0],[60,22],[100,34],[82,54],[100,78],[62,66],[50,100],[38,66],[0,78],[18,54],[0,34],[40,22]]),
  Marauder: fromCssPercent([[50,0],[66,14],[96,22],[100,46],[82,50],[82,78],[62,100],[62,64],[38,64],[38,100],[18,78],[18,50],[0,46],[4,22],[34,14]])
};
export const DEFAULT_ROLE_POLYGON = ROLE_POLYGON.Journeyman;

// ---- NPC hull shapes, by faction subtype ------------------------------
// Pirate: jagged/asymmetric. Faction: clean hex (Logi gets the support
// bulge). Alien is deliberately NOT a polygon -- see ALIEN_ELLIPSE below,
// same "organic, no hard edges" call as 16.78's CSS version.
export const NPC_POLYGON: Record<string, PolygonPoint[]> = {
  Raider: fromCssPercent([[50,0],[66,12],[100,30],[84,40],[96,62],[70,58],[62,90],[44,100],[40,68],[12,72],[20,46],[0,34],[30,22]]),
  Micro: fromCssPercent([[50,4],[76,20],[88,52],[62,58],[56,96],[30,84],[24,54],[4,46],[26,22]]),
  Leader: fromCssPercent([[50,0],[64,8],[92,16],[100,40],[80,44],[92,68],[66,64],[60,96],[40,100],[36,66],[8,72],[18,48],[0,40],[8,16],[36,10]]),
  Line: fromCssPercent([[50,0],[86,22],[86,62],[50,100],[14,62],[14,22]]),
  Logi: fromCssPercent([[50,0],[82,18],[100,40],[86,52],[96,70],[78,76],[50,100],[22,76],[4,70],[14,52],[0,40],[18,18]])
};

// Forward/side radii (fraction of `size`) for the two Alien subtypes --
// an ellipse rather than a point table, matching 16.78's border-radius
// blobs. Elite is larger and rounder (more "grown"); Duelist is a
// tighter teardrop.
export const ALIEN_ELLIPSE: Record<string, { forward: number; side: number }> = {
  Duelist: { forward: 0.85, side: 0.62 },
  Elite: { forward: 0.94, side: 0.74 }
};

export const FACTION_COLOR: Record<NpcFaction, { base: string; dim: string }> = {
  Pirate: { base: "#c0392b", dim: "#6e211a" }, // locked --sf-color-danger
  Faction: { base: "#4a7a8c", dim: "#2a4650" }, // new: cold, organized
  Alien: { base: "#9b59b6", dim: "#573265" } // new: deliberately "wrong" next to the other two
};

export const PLAYER_COLOR = { base: "#3aa0e8", dim: "#1f5c85" }; // locked --sf-color-shield
export const ESCORT_COLOR = { base: "#7fa8c9", dim: "#41627a" }; // locked --sf-color-escort family

export const TIER_NUMERAL: Record<NpcHullTier, string> = { T1: "I", T2: "II", T3: "III", T4: "IV" };
export const TIER_NUMBER: Record<NpcHullTier, 1 | 2 | 3 | 4> = { T1: 1, T2: 2, T3: 3, T4: 4 };

// Tier "plating" as a canvas fill/stroke recipe -- the same shape at a
// higher tier without changing hue, mirroring 16.78's CSS
// brightness/saturate filter + inset rim. brightness multiplies every
// channel of the base fill; rimAlpha/glow add the T3/T4 inset ring and
// T4's outer glow.
export interface TierPlating {
  brightness: number;
  rimAlpha: number;
  glow: boolean;
}
export const TIER_PLATING: Record<NpcHullTier, TierPlating> = {
  T1: { brightness: 0.92, rimAlpha: 0, glow: false },
  T2: { brightness: 1.0, rimAlpha: 0, glow: false },
  T3: { brightness: 1.08, rimAlpha: 0.35, glow: false },
  T4: { brightness: 1.16, rimAlpha: 0.55, glow: true }
};

export function lightenHex(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(((n >> 16) & 0xff) * factor);
  const g = clamp(((n >> 8) & 0xff) * factor);
  const b = clamp((n & 0xff) * factor);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// Strips a subtype name down to the bare key these tables use -- handles
// the hidden-system guardian's "Alien Elite (Ancient)" naming (16.70)
// the same way as an ordinary "Alien Elite", since it's still an Elite
// visually, just a tougher stat block.
export function npcSubtypeKey(unitName: string, faction: NpcFaction): string {
  const withoutParenthetical = unitName.replace(/\s*\([^)]*\)\s*$/, "");
  const withoutFactionPrefix = withoutParenthetical.replace(new RegExp(`^${faction}\\s+`), "");
  return withoutFactionPrefix.trim();
}

export type ShipShapeSpec =
  | { kind: "polygon"; points: PolygonPoint[]; colorBase: string; colorDim: string; plating: TierPlating; roman: string }
  | { kind: "ellipse"; forward: number; side: number; colorBase: string; colorDim: string; plating: TierPlating; roman: string; layered: boolean };

export function playerShipShape(role: string, hullTier: NpcHullTier, isFlown: boolean): ShipShapeSpec {
  const color = isFlown ? PLAYER_COLOR : ESCORT_COLOR;
  return {
    kind: "polygon",
    points: ROLE_POLYGON[role] ?? DEFAULT_ROLE_POLYGON,
    colorBase: color.base,
    colorDim: color.dim,
    plating: TIER_PLATING[hullTier],
    roman: TIER_NUMERAL[hullTier]
  };
}

export function npcShipShape(faction: NpcFaction, unitName: string, hullTier: NpcHullTier): ShipShapeSpec {
  const subtype = npcSubtypeKey(unitName, faction);
  const color = FACTION_COLOR[faction];
  const plating = TIER_PLATING[hullTier];
  const roman = TIER_NUMERAL[hullTier];

  if (faction === "Alien") {
    const ellipse = ALIEN_ELLIPSE[subtype] ?? ALIEN_ELLIPSE.Duelist;
    return {
      kind: "ellipse",
      forward: ellipse.forward,
      side: ellipse.side,
      colorBase: color.base,
      colorDim: color.dim,
      plating,
      roman,
      layered: subtype === "Elite"
    };
  }

  const points =
    faction === "Pirate"
      ? NPC_POLYGON[subtype] ?? NPC_POLYGON.Raider
      : NPC_POLYGON[subtype] ?? NPC_POLYGON.Line;
  return { kind: "polygon", points, colorBase: color.base, colorDim: color.dim, plating, roman };
}

// Canvas-drawn hull silhouettes (16.79; moved here from
// expeditionScreen.ts in 16.82, so the Trophy Room can draw the same
// real silhouettes the Expedition View does instead of a second, lesser
// icon system) -- the same shape LANGUAGE as
// 16.78's DOM/CSS icon system (src/ui/shipCanvasIcon.ts), re-expressed
// for `<canvas>` since that's how the Expedition View actually draws
// everything. Replaces the old shipShape(), which drew the exact same
// generic triangle for every ship regardless of role or faction, only
// ever recolored.
//
// `spec` carries WHAT to draw (shape points/ellipse, color, tier
// plating, numeral) -- resolved once per hostile/fleet-ship/player by
// the caller via playerShipShape()/npcShipShape(), not re-derived here.
// This function only does the actual drawing, in the local frame
// `points`/`forward`/`side` are already expressed in (nose at local
// +x, matching the ship's real heading after ctx.rotate(angle)).
export function drawShipShape(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  size: number,
  angle: number,
  spec: ShipShapeSpec
): void {
  const fill = lightenHex(spec.colorBase, spec.plating.brightness);

  ctx.save();
  ctx.translate(sx, sy);
  ctx.rotate(angle);

  if (spec.plating.glow) {
    ctx.shadowColor = spec.colorBase;
    ctx.shadowBlur = size * 0.6;
  }

  ctx.fillStyle = fill;
  if (spec.kind === "polygon") {
    ctx.beginPath();
    spec.points.forEach(([px, py], i) => {
      const method = i === 0 ? "moveTo" : "lineTo";
      ctx[method](px * size, py * size);
    });
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.ellipse(0, 0, spec.forward * size, spec.side * size, 0, 0, Math.PI * 2);
    ctx.fill();
    if (spec.layered) {
      // Elite's second inner curve (16.78's box-shadow-inset rings,
      // re-expressed as a stroked inner ellipse) -- the visual cue for
      // "armored/boss variant" that Duelist doesn't get.
      ctx.strokeStyle = spec.colorDim;
      ctx.lineWidth = Math.max(1, size * 0.08);
      ctx.beginPath();
      ctx.ellipse(0, 0, spec.forward * size * 0.72, spec.side * size * 0.72, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.shadowBlur = 0;

  // T3/T4's inset plating rim (16.78's box-shadow inset) -- a light
  // stroke just inside the hull's own edge, only once rimAlpha > 0.
  if (spec.plating.rimAlpha > 0) {
    ctx.strokeStyle = `rgba(191, 224, 247, ${spec.plating.rimAlpha})`;
    ctx.lineWidth = Math.max(1, size * 0.06);
    if (spec.kind === "polygon") {
      ctx.beginPath();
      spec.points.forEach(([px, py], i) => {
        const rimScale = 0.88;
        const method = i === 0 ? "moveTo" : "lineTo";
        ctx[method](px * size * rimScale, py * size * rimScale);
      });
      ctx.closePath();
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.ellipse(0, 0, spec.forward * size * 0.88, spec.side * size * 0.88, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.restore();

  // The tier numeral is drawn UNROTATED, in screen space -- a heading-
  // locked label would be unreadable half the time as a ship turns, the
  // same reason most top-down space games keep hull numbers upright.
  ctx.save();
  ctx.fillStyle = "rgba(5, 10, 18, 0.92)";
  ctx.font = `700 ${Math.max(9, size * 0.42)}px ui-monospace, "SF Mono", Consolas, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(spec.roman, sx, sy);
  ctx.restore();
}
